import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import React, { useCallback, useContext, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useOurSnackbar } from '_lib/utils/snackUtils';

import { BadRequestError, combineErrorsAndIssues, NotFoundError } from '../../api/apiErrors';
import { AppNameEnum } from '../../api/apiTypes.shim';
import { BackdropSpinner } from '../../components/BackdropSpinner/BackdropSpinner';

import { Box } from '../../components/Box/Box';
import { Button } from '../../components/Button/Button';
import { ErrorDisplay } from '../../components/ErrorDisplay/ErrorDisplay';
import { IconButton } from '../../components/IconButton/IconButton';
import { InputAdornment } from '../../components/InputAdornment/InputAdornment';
import { TextField } from '../../components/TextField/TextField';
import { Typography } from '../../components/Typography/Typography';

import { AuthSuccessResult, AuthTokenNotFound } from './auth.types';
import { AuthContext } from './authContext';

export interface ResetPasswordProps {
  appName: AppNameEnum;
  onResetPassword?: (result: AuthSuccessResult) => Promise<void>;
}

export const ResetPassword = (props: ResetPasswordProps): React.ReactElement => {
  const { appName, onResetPassword } = props;

  const navigate = useNavigate();
  const { resetPassword } = useContext(AuthContext);
  const { enqueueSnackbar } = useOurSnackbar();

  // TODO - move this resetToken logic to a proper loader
  const { resetToken } = useParams();
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const handleReset = useCallback(
    async (password: string, passwordConfirm: string) => {
      const errors = [];
      if (!password || !passwordConfirm) errors.push('password and password confirm are required');
      if (password !== passwordConfirm) errors.push('password confirm does not match password');
      if (errors.length) {
        setValidationErrors(errors);
        return;
      }

      setIsSaving(true);

      try {
        // TODO - when resetToken logic is moved to a proper loader we should not need this "as string"
        const result = await resetPassword(resetToken as string, { password, passwordConfirm }, appName, {
          disableSnackOnNotFoundError: true
        });
        if (onResetPassword) onResetPassword({ ...result, navigate });
      } catch (err) {
        if (err instanceof BadRequestError) {
          setValidationErrors(combineErrorsAndIssues(err.responseData?.nonFieldErrors, err.responseData?.fieldIssues));
        } else if (err instanceof AuthTokenNotFound) {
          setValidationErrors([err.messageToDisplay]);
        } else if (err instanceof NotFoundError) {
          enqueueSnackbar('Your reset password link has expired! Please resubmit your email');
          navigate('../forgot-password');
        }
      } finally {
        setIsSaving(false);
      }
    },
    [appName, enqueueSnackbar, navigate, onResetPassword, resetPassword, resetToken]
  );

  return (
    <>
      <BackdropSpinner open={isSaving} />
      {!!validationErrors.length && (
        <ErrorDisplay errors={validationErrors} invertColors padding={1} sx={{ margin: 1, borderRadius: 2 }} />
      )}
      <TextField
        fullWidth
        invertColors
        autoComplete='current-password'
        label='new password'
        size='medium'
        type={!showPassword ? 'password' : undefined}
        value={password}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position='start'>
                <LockIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            )
          }
        }}
        onChange={(e) => setPassword(e.target.value || '')}
        onEnter={() => handleReset(password, passwordConfirm)}
      />
      <TextField
        fullWidth
        invertColors
        autoComplete='confirm-password'
        label='confirm password'
        size='medium'
        type={!showPasswordConfirm ? 'password' : undefined}
        value={passwordConfirm}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position='start'>
                <LockIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}>
                  {showPasswordConfirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            )
          }
        }}
        onChange={(e) => setPasswordConfirm(e.target.value || '')}
        onEnter={() => handleReset(password, passwordConfirm)}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'end',
          width: '100%',
          marginY: 1
        }}>
        <Button
          type='button'
          variant='contained'
          color='primaryInverted'
          onClick={() => handleReset(password, passwordConfirm)}
          disabled={!password || !passwordConfirm}>
          <Typography variant='body1Bold' text='Reset' />
        </Button>
      </Box>
    </>
  );
};
